import React from 'react';
import { Circle, G, Line, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/**
 * Pitot traverse averaging in a round duct using log-Tchebycheff sampling positions.
 * Shows duct cross-section + concentric area markers, traverse positions on two
 * perpendicular axes, pitot probe at one position with insertion-depth indicator,
 * U-tube manometer reading VP, and the velocity averaging callout.
 */
export function DiagramPitotTraverseAveraging() {
  const cx = 130, cy = 125, R = 70;

  // Log-Tchebycheff fractions of diameter for 6-point traverse (per axis)
  const fractions = [0.032, 0.135, 0.321, 0.679, 0.865, 0.968];
  // Convert to vertical positions on the diameter
  const yPositions = fractions.map(f => cy - R + f * 2 * R);
  const xPositions = fractions.map(f => cx - R + f * 2 * R);

  // Selected probe insertion (third point)
  const probeY = yPositions[2];

  return (
    <DiagramCanvas>
      {/* Duct outer wall (round cross-section) */}
      <Circle cx={cx} cy={cy} r={R} fill={DT.fill} stroke={DT.stroke} strokeWidth="2" />
      {/* Inner concentric rings — equal-area zones */}
      {[0.4, 0.65, 0.85].map((f, i) => (
        <Circle key={i} cx={cx} cy={cy} r={R * f} fill="none"
          stroke={DT.dim} strokeWidth="0.7" strokeDasharray="2,3" opacity={0.7} />
      ))}
      {/* Crosshair traverse axes */}
      <Line x1={cx - R} y1={cy} x2={cx + R} y2={cy} stroke={DT.dim} strokeWidth="0.7" strokeDasharray="2,2" />
      <Line x1={cx} y1={cy - R} x2={cx} y2={cy + R} stroke={DT.dim} strokeWidth="0.7" strokeDasharray="2,2" />

      {/* Sample points along vertical axis */}
      {yPositions.map((yp, i) => (
        <G key={`v${i}`}>
          <Circle cx={cx} cy={yp} r={3} fill={DT.varColor} />
          <SvgText x={cx + 5} y={yp - 4} fill={DT.desc} fontSize="7" fontFamily={DT.mono}>{i + 1}</SvgText>
        </G>
      ))}
      {/* Sample points along horizontal axis */}
      {xPositions.map((xp, i) => (
        <Circle key={`h${i}`} cx={xp} cy={cy} r={3} fill={DT.varColor} opacity={0.85} />
      ))}

      {/* Pitot probe inserted at point #3 from top */}
      <Line x1={cx} y1={cy - R - 25} x2={cx} y2={probeY + 4}
        stroke={DT.leader} strokeWidth="3" />
      {/* L-tip facing flow (out-of-page → drawn left) */}
      <Line x1={cx - 12} y1={probeY} x2={cx} y2={probeY} stroke={DT.leader} strokeWidth="3" />
      <Circle cx={cx - 12} cy={probeY} r={2.5} fill={DT.varColor} />

      {/* Insertion-depth indicator */}
      <Line x1={cx + 18} y1={cy - R} x2={cx + 18} y2={probeY} stroke={DT.dim} strokeWidth="1" />
      <Polygon points={`${cx + 18},${cy - R} ${cx + 15},${cy - R + 4} ${cx + 21},${cy - R + 4}`} fill={DT.dim} />
      <Polygon points={`${cx + 18},${probeY} ${cx + 15},${probeY - 4} ${cx + 21},${probeY - 4}`} fill={DT.dim} />
      <SvgText x={cx + 22} y={(cy - R + probeY) / 2 + 3} fill={DT.dim} fontSize="8" fontFamily={DT.mono}>depth</SvgText>

      {/* Manometer (top right) */}
      <G>
        <Rect x={245} y={40} width={26} height={70} fill="none" stroke={DT.stroke} strokeWidth="1.2" />
        <Rect x={246} y={70} width={11} height={39} fill={DT.varColor} opacity={0.7} />
        <Rect x={246} y={98} width={24} height={11} fill={DT.varColor} opacity={0.7} />
        <Rect x={260} y={85} width={10} height={24} fill={DT.varColor} opacity={0.7} />
        <Line x1={240} y1={70} x2={240} y2={85} stroke={DT.varColor} strokeWidth="1.5" />
        <Polygon points="240,70 237,74 243,74" fill={DT.varColor} />
        <Polygon points="240,85 237,81 243,81" fill={DT.varColor} />
        <SvgText x={236} y={80} fill={DT.varColor} fontSize="9"
          fontFamily={DT.mono} fontWeight="700" textAnchor="end">VP</SvgText>
        <SvgText x={258} y={36} fill={DT.desc} fontSize="8.5" fontFamily={DT.mono} textAnchor="middle">manometer</SvgText>
      </G>
      {/* Tubing pitot → manometer */}
      <Line x1={cx} y1={cy - R - 25} x2={cx + 30} y2={cy - R - 35} stroke={DT.leader} strokeWidth="1" />
      <Line x1={cx + 30} y1={cy - R - 35} x2={258} y2={110} stroke={DT.leader} strokeWidth="1" />

      {/* Cardinal marks */}
      <SvgText x={cx} y={cy - R - 30} fill={DT.desc} fontSize="8" fontFamily={DT.mono} textAnchor="middle">access port</SvgText>

      {/* Labels */}
      <LeaderLine x1={cx - 12} y1={probeY} x2={20} y2={210} varName="VP_i" desc="Reading at point i" anchor="start" />
      <LeaderLine x1={xPositions[5]} y1={cy} x2={130} y2={210} varName="V_i" desc="V_i = 4005√VP_i" anchor="middle" />
      <LeaderLine x1={cx + R * 0.7} y1={cy + R * 0.7} x2={310} y2={210} varName="V_avg" desc="Σ V_i / n" anchor="end" />

      <FormulaTag formula="V_avg = Σ(4005·√VP_i) / n   (log-Tchebycheff)" />
    </DiagramCanvas>
  );
}
